import React, { useState, useEffect } from 'react';
import { LessonSummary } from '../../services/lessonServiceTypes';
import './GenericHistorySelector.css';

interface LevelConfig<T> {
  options: Array<{ value: T; label: string }>;
  defaultLevel: T;
  label: string;
}

// Base props
interface GenericHistorySelectorBaseProps {
  isOpen: boolean;
  pastLessons: LessonSummary[];
  onClose: () => void;
  
  // Optional configurations
  title?: string;
  customInstructionLabel?: string;
  customInstructionPlaceholder?: string;
  showImageOption?: boolean; // Optional flag to show image generation checkbox
}

// Props without level support
export interface GenericHistorySelectorPropsWithoutLevel extends GenericHistorySelectorBaseProps {
  onGenerate: (selectedIds: string[], customInstruction: string, generateImage?: boolean) => void;
  levelConfig?: never;
  extractLessonLevel?: never;
  getLevelDisplayName?: never;
}

// Props with level support
export interface GenericHistorySelectorPropsWithLevel<TLevel extends string> extends GenericHistorySelectorBaseProps {
  onGenerate: (level: TLevel, selectedIds: string[], customInstruction: string, generateImage?: boolean) => void;
  levelConfig: LevelConfig<TLevel>;
  extractLessonLevel?: (objective: string) => TLevel | null;
  getLevelDisplayName?: (level: TLevel) => string;
}

// Union type for all props
export type GenericHistorySelectorProps<TLevel extends string = string> = 
  | GenericHistorySelectorPropsWithoutLevel
  | GenericHistorySelectorPropsWithLevel<TLevel>;

function GenericHistorySelector<TLevel extends string = string>(
  props: GenericHistorySelectorProps<TLevel>
): JSX.Element | null {
  const {
    isOpen,
    pastLessons,
    onGenerate,
    onClose,
    title = 'Generate New Lesson',
    customInstructionLabel = 'What topic would you like to learn about? (Optional)',
    customInstructionPlaceholder = 'e.g., Shopping at a market, Introducing yourself...',
    showImageOption = false,
    levelConfig,
    extractLessonLevel,
    getLevelDisplayName
  } = props;

  const [customInstruction, setCustomInstruction] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [level, setLevel] = useState<TLevel | undefined>(levelConfig?.defaultLevel);
  const [generateImage, setGenerateImage] = useState(false);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setCustomInstruction('');
      setSelectedIds(new Set());
      setGenerateImage(false);
      if (levelConfig) {
        setLevel(levelConfig.defaultLevel);
      }
    }
  }, [isOpen, levelConfig]);

  if (!isOpen) return null;

  const handleSelectAll = () => {
    setSelectedIds(new Set(pastLessons.map(l => l.lesson_id)));
  };

  const handleDeselectAll = () => {
    setSelectedIds(new Set());
  };

  const handleToggleLesson = (lessonId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(lessonId)) {
      newSelected.delete(lessonId);
    } else {
      newSelected.add(lessonId);
    }
    setSelectedIds(newSelected);
  };

  const handleGenerate = () => {
    const instruction = customInstruction.trim();
    const ids = Array.from(selectedIds);
    
    if (levelConfig && level !== undefined) {
      // Call with level parameter
      (onGenerate as (level: TLevel, selectedIds: string[], customInstruction: string, generateImage?: boolean) => void)(
        level,
        ids,
        instruction,
        generateImage
      );
    } else {
      // Call without level parameter
      (onGenerate as (selectedIds: string[], customInstruction: string, generateImage?: boolean) => void)(
        ids,
        instruction,
        generateImage
      );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const truncateText = (text: string, maxLength: number) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  const renderLessonItem = (lesson: LessonSummary) => {
    // Extract level if helper functions are provided
    let lessonLevel: TLevel | null = null;
    let objectiveText = lesson.lesson_objective;

    if (extractLessonLevel && getLevelDisplayName) {
      lessonLevel = extractLessonLevel(lesson.lesson_objective);
      // Remove level prefix from objective for display
      objectiveText = lesson.lesson_objective.replace(/\[Level:\s*\w+\]\s*/, '');
    }

    return (
      <label 
        key={lesson.lesson_id} 
        className="history-selector-lesson-item"
      >
        <input
          type="checkbox"
          className="history-selector-checkbox"
          checked={selectedIds.has(lesson.lesson_id)}
          onChange={() => handleToggleLesson(lesson.lesson_id)}
        />
        <div className="history-selector-lesson-info">
          {lessonLevel && getLevelDisplayName ? (
            <div className="history-selector-lesson-header">
              <div className="history-selector-lesson-name">
                {lesson.lesson_name}
              </div>
              <span className={`history-selector-level-badge level-${lessonLevel}`}>
                {getLevelDisplayName(lessonLevel)}
              </span>
            </div>
          ) : (
            <div className="history-selector-lesson-name">
              {lesson.lesson_name}
            </div>
          )}
          <div className="history-selector-lesson-objective">
            {truncateText(objectiveText, 80)}
          </div>
          <div className="history-selector-lesson-date">
            {formatDate(lesson.created_at)}
          </div>
        </div>
      </label>
    );
  };

  return (
    <>
      <div className="history-selector-overlay" onClick={onClose} />
      <div className="history-selector-modal">
        <div className="history-selector-header">
          <h2 className="history-selector-title">{title}</h2>
          <button 
            className="history-selector-close" 
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="history-selector-content">
          {/* Level Selection (if configured) */}
          {levelConfig && (
            <div className="history-selector-section">
              <label htmlFor="level-select" className="history-selector-label">
                {levelConfig.label}
              </label>
              <select
                id="level-select"
                className="history-selector-dropdown"
                value={level as string}
                onChange={(e) => setLevel(e.target.value as TLevel)}
              >
                {levelConfig.options.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Custom Instruction Input */}
          <div className="history-selector-section">
            <label htmlFor="custom-instruction" className="history-selector-label">
              {customInstructionLabel}
            </label>
            <input
              id="custom-instruction"
              type="text"
              className="history-selector-input"
              placeholder={customInstructionPlaceholder}
              value={customInstruction}
              onChange={(e) => setCustomInstruction(e.target.value)}
            />
          </div>

          {/* Past Lessons Selection */}
          {pastLessons.length > 0 && (
            <div className="history-selector-section">
              <div className="history-selector-lessons-header">
                <span className="history-selector-label">
                  Select past lessons to avoid repeating content
                </span>
                <div className="history-selector-actions">
                  <button 
                    className="history-selector-action-btn"
                    onClick={handleSelectAll}
                  >
                    Select All
                  </button>
                  <button 
                    className="history-selector-action-btn"
                    onClick={handleDeselectAll}
                  >
                    Deselect All
                  </button>
                </div>
              </div>

              <div className="history-selector-lessons-list">
                {pastLessons.map(renderLessonItem)}
              </div>
            </div>
          )}

          {pastLessons.length === 0 && (
            <div className="history-selector-empty">
              <p>No past lessons found. Your first lesson will be generated without context.</p>
            </div>
          )}

          {/* Image Generation Option */}
          {showImageOption && (
            <div className="history-selector-section history-selector-image-option">
              <label className="history-selector-checkbox-label">
                <input
                  type="checkbox"
                  className="history-selector-checkbox-input"
                  checked={generateImage}
                  onChange={(e) => setGenerateImage(e.target.checked)}
                />
                <span className="history-selector-checkbox-text">
                  Generate AI image for this lesson
                </span>
              </label>
              <p className="history-selector-image-hint">
                An illustrative image will be generated using AI and displayed with the lesson content.
              </p>
            </div>
          )}
        </div>

        <div className="history-selector-footer">
          <button 
            className="history-selector-btn history-selector-btn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="history-selector-btn history-selector-btn-generate"
            onClick={handleGenerate}
          >
            Generate Lesson
          </button>
        </div>
      </div>
    </>
  );
}

export default GenericHistorySelector;
